import { WebhusetApiError } from "~/models";
import type { BaseApiResult } from "~/types";

interface WebhusetApiResponse<T = any> {
  // data: T | null;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  data: Ref<T>;
  restGet<T extends BaseApiResult>(
    url: string,
    anonymous?: boolean
  ): Promise<T>;
  restDelete<T extends BaseApiResult>(
    url: string,
    anonymous?: boolean
  ): Promise<T>;
  restPut<T extends BaseApiResult>(
    url: string,
    payload: Record<string, unknown>,
    anonymous?: boolean
  ): Promise<T>;
  restPost<T extends BaseApiResult>(
    url: string,
    payload: Record<string, unknown>,
    anonymous?: boolean
  ): Promise<T>;
}

export function useWebhusetApi<T>(): WebhusetApiResponse {
  const data = ref<T>();
  const loading = ref<boolean>(true);
  const error = ref<string | null>(null);
  const config = useRuntimeConfig();
  const apiBase = config.public.apiServer;

  function getHeaders(anonymous: boolean) {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (!anonymous) {
      const authStore = useAuthStore();

      const apiKey = authStore.apiKey;
      const encodedCredentials = btoa(`${apiKey}:''}`);

      headers.Authorization = `Basic ${encodedCredentials}`;

      if (authStore.isPartner && authStore.customerId) {
        headers["Impersonate-Customer-Id"] = authStore.customerId.toString();
      }
    }

    return headers;
  }

  async function checkResponse<T extends BaseApiResult>(
    response: Response,
    resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: any) => void
  ) {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const parsed = (await response.json()) as T;

    if (parsed.status != 1) {
      reject(new WebhusetApiError(parsed.errcode));
    } else {
      resolve(parsed);
    }
  }

  function restGet<T extends BaseApiResult>(
    url: string,
    anonymous: boolean = false
  ): Promise<T> {
    loading.value = true;
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(apiBase + url, {
          method: "GET",
          headers: getHeaders(anonymous),
        });

        checkResponse<T>(response, resolve, reject);
      } catch (err: any) {
        error.value = err.message;
        reject(new Error(err.message));
      } finally {
        loading.value = false;
      }
    });
  }

  function restDelete<T extends BaseApiResult>(
    url: string,
    anonymous: boolean = false
  ): Promise<T> {
    loading.value = true;
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(apiBase + url, {
          method: "DELETE",
          headers: getHeaders(anonymous),
        });

        checkResponse<T>(response, resolve, reject);
      } catch (err: any) {
        error.value = err.message;
        reject(err);
      } finally {
        loading.value = false;
      }
    });
  }

  function restPost<T extends BaseApiResult>(
    url: string,
    payload: Record<string, unknown>,
    anonymous: boolean = false
  ): Promise<T> {
    loading.value = true;
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(apiBase + url, {
          method: "POST",
          headers: getHeaders(anonymous),
          body: JSON.stringify(payload),
        });

        checkResponse<T>(response, resolve, reject);
      } catch (err: any) {
        reject(err);
      } finally {
        loading.value = false;
      }
    });
  }

  function restPut<T extends BaseApiResult>(
    url: string,
    payload: Record<string, unknown>,
    anonymous: boolean = false
  ): Promise<T> {
    loading.value = true;
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(apiBase + url, {
          method: "PUT",
          headers: getHeaders(anonymous),
          body: JSON.stringify(payload),
        });

        checkResponse<T>(response, resolve, reject);
      } catch (err: any) {
        reject(err);
      } finally {
        loading.value = false;
      }
    });
  }

  return {
    loading,
    error,
    data,
    restGet,
    restDelete,
    restPut,
    restPost,
  };
}
